import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

// "+" icon — same path as the pillar's Add New Program button, reused here.
const PlusIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 3.75V14.25M3.75 9H14.25"
      stroke="#FDFDFD"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * "Add new column" tile — Figma spec:
 *   • Column dimensions match a pillar column (bg #1C1C1C, right border #2A2A2A)
 *   • Empty header area (padding 20)
 *   • Body centred: 38x38 + button (bg #565656, radius 6, 1px #0F0F0F border)
 *     and "Add new column" link text below (Inter 14/600 #FDFDFD)
 * Shown in Edit Framework mode, always at the end of the column stack.
 */
const AddNewColumnTile: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Stack
    onClick={onClick}
    sx={{
      // Same 25% width as the pillar columns. With the default 4 pillars,
      // this tile becomes the 5th element and lives just off the right
      // edge of the viewport — the user scrolls to it via the arrow.
      flex: '0 0 25%',
      minWidth: 0,
      height: '100%',
      bgcolor: '#1C1C1C',
      borderRight: '1px solid #2A2A2A',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      '&:hover .ll-add-column-button': { bgcolor: '#666666' }
    }}
  >
    {/* Empty header area to align with the editable headers in adjacent
        columns (padding 20 matches the pillar column wrapper padding). */}
    <Box sx={{ p: '20px' }} />

    {/* Centred + button and label */}
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        p: '10px'
      }}
    >
      <Box
        className="ll-add-column-button"
        sx={{
          width: 38,
          height: 38,
          pl: '12px',
          pr: '12px',
          pt: '10px',
          pb: '10px',
          bgcolor: '#565656',
          border: '1px solid #0F0F0F',
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.15s'
        }}
      >
        <PlusIcon />
      </Box>
      <Typography
        sx={{
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          fontWeight: 600
        }}
      >
        Add new column
      </Typography>
    </Box>
  </Stack>
);

export default AddNewColumnTile;
